
/** @see https://developers.google.com/youtube/iframe_api_reference */

import { ComputedRef, defineComponent, onBeforeUnmount, onMounted, Ref, ref, watch } from "vue";
import {dispatchError} from "../../utils/error_handling";
import {Content, ContentAnnotation, SecondsCheckpoint} from "./helpers";
import {ContentType, Youtube} from "../../../../domain/entities/content/content";
import {computed} from "@vue/reactivity";
import {useStore} from "vuex";
import ContentAlertModal from "../../components/application/modals/ContentAlertModal.vue";
import {onBeforeRouteLeave} from "vue-router";
import ContentFallback from "../../components/application/moldure/ContentFallback.vue";
import {AnnotationObserver, annotationSubject} from "./menu/ContentAnnotationNotifier";
import player from "../../../repository/store/modules/content/player";

type YTWindow = {
  YT: {
    Player: NewPlayer | any;
    PlayerState: {
      UNSTARTED: number;
      ENDED: number;
      PLAYING: number;
      PAUSED: number;
      BUFFERING: number;
      CUED: number;
    }
  }
  onYouTubeIframeAPIReady: () => void;
  onPlaybackRateChange: () => void;
}

interface NewPlayer {
  id: string,
  configuration: {
    width: string;
    height: string;
    videoId: string;
    playerVars: {
      modestbranding: number;
      rel: number
    };
    events: {
      onStateChange: (event: YouTubeStateEvent) => Promise<void>;
      onReady: () => Promise<void>
    };
  };
}

interface Player {
  /** Plays the currently indicated/uploaded video. The final state of the player after this function is performed will be (1). */
  playVideo: () => void;

  /** Pauses the video that is playing. After this function is executed, the final state of the player will be (), unless the player is in the state () when the function is called, in which case the state of the player will not change. */
  pauseVideo: () => void;

  /** This function loads and plays the specified video. */
  loadVideoById: (configuration: loadVideoConfiguration) => void;

  /** Returns the elapsed time in seconds since the video starts playing. */
  getCurrentTime: () => number;

  /** Retrieve the playback rate for the cued or playing video.*/
  getPlaybackRate: () => number;

  /** Return the player state */
  getPlayerState: () => PlayerState;

  /** Looks for a specified point in the video. If the player is paused when the function is called, it remains paused. If the function is called from another state, the player plays the video.*/
  seekTo: (seconds: number, allowSeekAhead: boolean) => void;

  /** Add a listener function to the specified event. */
  addEventListener: (event: string, listener: () => void) => void;
}

type loadVideoConfiguration = {
  /** he required parameter specifies the ID of the YouTube video to play. In the YouTube Data API video feeds, the tag specifies the ID. */
  videoId: string,

  /** he required parameter specifies the ID of the YouTube video to play. In the YouTube Data API video feeds, the tag specifies the ID. */
  startSeconds?: number,

  /** The optional parameter accepts a decimal number / integer. If specified, the video will stop playing at the specified point. */
  endSeconds?: number,

  /** The optional parameter specifies the suggested playback quality for the video. See the function definition to learn more about playback quality */
  suggestedQuality?: "small" | "medium" | "large" | "hd720" | "hd1080" | "highres" | "default",
}

type YouTubeStateEvent = {
  data: PlayerState,
}

enum PlayerState {
  UNSTARTED = -1,
  ENDED = 0,
  PLAYING = 1,
  PAUSED = 2,
  BUFFERING = 3,
  CUED = 5
}

class YoutubeViewer implements AnnotationObserver {
  public YTWindow: YTWindow;
  public player: Player;

  constructor(YTWindow: YTWindow, player: Player) {
    this.YTWindow = YTWindow;
    this.player = player;
  }

  public onNoteCreated(note: ContentAnnotation) {
    if(!this.player) return;
    if (this.player.getPlayerState() === this.YTWindow.YT.PlayerState.PLAYING) return;
    if (this.player.getPlayerState() === this.YTWindow.YT.PlayerState.ENDED) return;
    this.player.playVideo();
  }

  public onNoteUpdated(note: ContentAnnotation) {
    if(!this.player) return;
    if (this.player.getPlayerState() === this.YTWindow.YT.PlayerState.PLAYING) return;
    if (this.player.getPlayerState() === this.YTWindow.YT.PlayerState.ENDED) return;
    this.player.playVideo();
  }

  public onNoteDeleted(note: ContentAnnotation) {
  }

  public onNoteSelected(note: ContentAnnotation) {
    if(!this.player) return;
    if (!note.contentTimestamp) return;
    this.player.seekTo(note.contentTimestamp, true);
  }

  public onTakingNote(note: ContentAnnotation) {
    if(!this.player) return;
    if (this.player.getPlayerState() === this.YTWindow.YT.PlayerState.PAUSED) return;
    this.player.pauseVideo();
  }
}

export default defineComponent({
  name: "YouTubeViewer",
  components: {ContentFallback, ContentAlertModal},
  setup() {
    const store = useStore();
    const YTWindow = window as unknown as YTWindow;

    let youtubeViewer: YoutubeViewer;

    const isYouTubeRendered: Ref<boolean> = ref(true);

    const disposeRoutines = async () => {
      stopCheckpointRoutine();
      stopSeekTrackerInterval();
      await stopProgressRoutine();
      youtubeViewer?.player?.pauseVideo?.();
    };

    onMounted(() => {
      const handleVisibility = async () => {
        if (document.visibilityState === "hidden") {
          await disposeRoutines();
        } else if (document.visibilityState === "visible") {
          if (!youtubeViewer?.player || typeof youtubeViewer.player.playVideo !== "function") {
            await loadYouTubePlayer();
          }
          await startProgressRoutine();
          startSeekTracker();
          youtubeViewer?.player?.playVideo?.();
        }
      };

      document.addEventListener("visibilitychange", handleVisibility);

      handleVisibility();

      onBeforeUnmount(() => {
        document.removeEventListener("visibilitychange", handleVisibility);
      });
    });

    const currentContent: ComputedRef<Content> = computed(() => store.getters["content_module/currentContent"]);

    watch(currentContent, async () => await onCurrentContentChange());

    /** on current content change clear all previous routines, load the player and start a new progress routine. */
    const onCurrentContentChange = async () => {
      try {
        stopCheckpointRoutine();
        stopSeekTrackerInterval();
        await stopProgressRoutine();
        if (currentContent.value.type !== ContentType.YOUTUBE) {
          return
        }
        await loadYouTubePlayer();
        await startProgressRoutine();
        initializeSeekTracker();
      } catch (e) {
        console.error("[onCurrentContentChange] Error ", e);
        await dispatchError(e);
      }
    };

    const loadYouTubePlayer = async (): Promise<void> => {
      try {
        const script = document.getElementById(scriptID);
        if (!script) {
          createScript();
        } else if (!youtubeViewer) {
          const player = await createPlayer();
          initializeYouTubePlayerViewer(YTWindow, player);
        } else {
          await updateVideoOnPlayer();
        }
      } catch (e) {
        isYouTubeRendered.value = false;
        console.error("[onCurrentContentChange] Error ", e);
        throw e;
      }
    };

    /** Used to know if the script is already loaded. */
    const scriptID = "youtube-script";

    /** This code loads the IFrame Player API code asynchronously. */
    const createScript = () => {
      const script = document.createElement('script');
      script.src = "https://www.youtube.com/iframe_api";
      script.id = scriptID;

      const firstScriptTag = document.getElementsByTagName('script')[0];
      if (!firstScriptTag) throw new Error("First script element on DOM where not found");
      if (!firstScriptTag.parentNode) throw new Error("First script parent node element on DOM where not found");
      firstScriptTag.parentNode.insertBefore(script, firstScriptTag);
    };

    const initializeYouTubePlayerViewer = (YTWindow: YTWindow, player: Player) => {
      youtubeViewer = new YoutubeViewer(YTWindow, player);
      annotationSubject.subscribe(youtubeViewer);
    };

    /** This function creates an <iframe> (and YouTube player) after the API code downloads. */
    YTWindow.onYouTubeIframeAPIReady = async (): Promise<void> => {
      try {
        const player = await createPlayer();
        initializeYouTubePlayerViewer(YTWindow, player);
      } catch (e) {
        console.error("[onYouTubeIframeAPIReady] Error ", e);
        await dispatchError(e);
      }
    };

    /** Create the YouTube player with all necessary configurations. */
    const createPlayer = async (): Promise<Player> => {
      try {
        const {code} = getYouTubeFromContentSafety();
        // noinspection TypeScriptValidateTypes
        return new YTWindow.YT.Player('player', {
          height: '100%',
          width: '100%',
          videoId: code,
          playerVars: {modestbranding: 1, rel: 0},
          events: {
            onReady: onPlayerReady,
            onStateChange: onPlayerStateChange
          }
        });
      } catch (e) {
        console.error("[createYouTubePlayer] Error ", e);
        throw e;
      }
    };


    const getYouTubeFromContentSafety = (): Youtube => {
      const content = getCurrentContentSafety();
      if (!content.youtube) throw new Error("content.youtube is null");
      return content.youtube
    };


    /** Return the current content object from the currentContent computed value.
     * If the computed value is null throws error.
     * @throws Error. */
    const getCurrentContentSafety = (): Content => {
      if (!currentContent.value) throw new Error("currentContent.value is null");
      return currentContent.value;
    };

    /** PLay the YouTube Player when the YouTubeScript is ready. */
    const onPlayerReady = async (): Promise<void> => {
      try {
        const checkpoint = getYouTubeCheckpointSafety();
        youtubeViewer.player.seekTo(checkpoint.timestamp, true);
        youtubeViewer.player.playVideo();
      } catch (e) {
        console.error("[onPlayerReady] Error ", e);
        await dispatchError(e, "Erro ao carregar o vídeo");
      }
    };

    /** Load the new video on the player. */
    const updateVideoOnPlayer = async (): Promise<void> => {
      try {
        const {code} = getYouTubeFromContentSafety();
        const checkpoint = getYouTubeCheckpointSafety();
        if (!youtubeViewer.player.loadVideoById) {
          return
        }
        youtubeViewer.player.loadVideoById({videoId: code, startSeconds: checkpoint.timestamp});
      } catch (e) {
        console.error("[updateVideoOnPlayer] Error ", e);
        await dispatchError(e, "Erro ao carregar o vídeo");
      }
    };

    /** Return the YouTube object from the currentContent computed value.
     * If the computed value is null throws error.
     * @throws Error. */
    const getYouTubeCheckpointSafety = (): SecondsCheckpoint => {
      const content = getCurrentContentSafety();
      const checkpoint = content.status.secondsCheckpoint;
      if (!checkpoint) throw new Error("content.status.secondsCheckpoint is null");
      return checkpoint;
    };

    /** The API calls this function when the player's state changes. */
    const onPlayerStateChange = async ({data}: YouTubeStateEvent): Promise<void> => {
      switch (data) {
        case PlayerState.PLAYING:
          await onPlay();
          break;
        case PlayerState.PAUSED:
        case PlayerState.ENDED:
        case PlayerState.BUFFERING:
          await onPause();
          break;
      }
    };

    const onPlay = async (): Promise<void> => {
      try {
        await startCheckpointRoutine();
        startSeekTracker();
      } catch (e) {
        console.error("[onPlay] Error ", e);
        await dispatchError(e);
      }
    };

    let checkpointInterval;
    const startCheckpointRoutine = (): void => {
      checkpointInterval = setInterval(saveCheckpointSeconds, 5000);
    };

    const saveCheckpointSeconds = async (): Promise<void> => {
      try {
        if (!youtubeViewer.player) {
          return;
        }
        const timestamp = Math.round(youtubeViewer.player.getCurrentTime());
        await store.dispatch("content_module/player/checkpoint/saveCheckpointSeconds", timestamp);
      } catch (e) {
        console.error("[saveCheckpointSeconds] Error ", e);
        await dispatchError(e);
      }
    };

    let seekTrackerInterval;
    let seekTrackerHistory: number[] = [];
    let seekTrackerMaxProgress: number = 0;
    const showSeekerModal: Ref<boolean> = ref(false);
    let didShowSeekerModal: boolean = false;

    const startSeekTracker = () => {
      if (!currentContent.value.minDuration) {
        return
      }

      if (currentContent.value.status.finishedAt) {
        return
      }

      seekTrackerInterval = setInterval(() => {
        if (!youtubeViewer.player) {
          return;
        }
        const currentTime = youtubeViewer.player.getCurrentTime();

        if (seekTrackerHistory.length > 0) {
          const last = seekTrackerHistory[seekTrackerHistory.length - 1];

          const playbackRate: number = youtubeViewer.player.getPlaybackRate();
          const userSkip = currentTime - last > playbackRate + 0.5;
          const exceededMaxProgress = currentTime > seekTrackerMaxProgress;

          if (exceededMaxProgress) {
            if (!userSkip) {
              seekTrackerMaxProgress = currentTime;
            } else if (!didShowSeekerModal) {
              showSeekerModal.value = true;
            }
          }
        }

        seekTrackerHistory.push(currentTime);
        seekTrackerHistory = seekTrackerHistory.slice(-3);
      }, 500);
    };

    const initializeSeekTracker = (): void => {
      const {timestamp} = getYouTubeCheckpointSafety();
      seekTrackerMaxProgress = timestamp;
      seekTrackerHistory = [];
      showSeekerModal.value = false;
      didShowSeekerModal = false;
    }

    const onPause = async (): Promise<void> => {
      try {
        stopSeekTrackerInterval();
        stopCheckpointRoutine();
        await saveLastPausedTime();
      } catch (e) {
        console.error("[onPause] Error ", e);
        await dispatchError(e);
      }
    };

    const stopCheckpointRoutine = () => {
      try {
        clearInterval(checkpointInterval);
      } catch (e) {
        console.error("[stopCheckpointRoutine] Error ", e);
        throw e;
      }
    };

    const saveLastPausedTime = async () => {
      try {
        await store.dispatch("content_module/player/setLastPausedTime", Math.round(youtubeViewer.player.getCurrentTime()));
      } catch (e) {
        console.error("[saveLastPausedTime] Error ", e);
        throw e;
      }
    }

    const startProgressRoutine = async (): Promise<void> => {
      try {
        await store.dispatch("content_module/player/progress/startProgressRoutine");
      } catch (e) {
        console.error("[startProgressRoutine] Error ", e);
        throw e;
      }
    };

    const stopProgressRoutine = async (): Promise<void> => {
      try {
        await store.dispatch("content_module/player/progress/stopProgressRoutine");
      } catch (e) {
        console.error("[stopProgressRoutine] Error ", e);
        throw e;
      }
    };

    const stopSeekTrackerInterval = (): void => {
      clearInterval(seekTrackerInterval);
      if (!youtubeViewer || !youtubeViewer.player) {
        return
      }

      if (isYouTubeRendered.value) {
        return;
      }

      const lastTime = seekTrackerHistory[seekTrackerHistory.length - 1];
      const currentTime = youtubeViewer.player.getCurrentTime();

      if (currentTime - lastTime <= 1.5) {
        seekTrackerHistory.push(currentTime);
        seekTrackerHistory = seekTrackerHistory.slice(-3);
      }
    }

    const onCloseSeekModal = () => {
      showSeekerModal.value = false;
      didShowSeekerModal = true;
    }

    /** This event fires whenever the video playback rate changes. */
    YTWindow.onPlaybackRateChange = async (): Promise<void> => {
      try {
        await updatePlaybackRate();
      } catch (e) {
        console.error("[onPlaybackRateChange] Error ", e);
        await dispatchError(e);
      }
    };

    const updatePlaybackRate = async (): Promise<void> => {
      try {
        await store.dispatch("content_module/player/setPlaybackRate", youtubeViewer.player.getPlaybackRate());
      } catch (e) {
        console.error("[updatePlaybackRate] Error ", e);
        throw e;
      }
    };

    onBeforeRouteLeave(async () => {
      try {
        stopCheckpointRoutine();
        stopSeekTrackerInterval();
        await stopProgressRoutine();
      } catch (e) {
        console.error("[onBeforeRouteLeave] Error ", e);
        await dispatchError(e);
      }
    })

    return {
      didShowSeekerModal: showSeekerModal,
      isYouTubeRendered,
      onCloseSeekModal,
    }
  }
});
